import { apiClient, imageUploadService } from '@kss/api';

/**
 * Convert a possibly-data-URL form value into a persisted hosted URL
 * by POSTing to `/api/Image/upload` when the value starts with
 * `data:`. Anything else (regular `http(s)` URL, or empty) is
 * returned unchanged.
 *
 * The backend ships the upload result as a TENANT-RELATIVE path
 * like `/uploads/{tenantId}/misc/2026/05/abc.png`. We resolve it to
 * the API host's absolute URL so the value works in any context
 * (storefront on `sso.kristysoftware.com`, dev preview on
 * `localhost:5173`, an export pipeline, …).
 *
 * Throws when the upload fails so the calling form can surface a
 * proper toast — callers should `await` and `try/catch`.
 */
export async function persistImage(
  rawValue: string | null | undefined,
): Promise<string | undefined> {
  if (!rawValue) return undefined;
  const v = rawValue.trim();
  if (!v) return undefined;
  // Already a hosted URL? pass through.
  if (!v.startsWith('data:')) return v;

  // Decode data URL → Blob and pick a friendly filename + MIME type.
  const match = v.match(/^data:([^;,]+)(?:;([^,]+))?,(.+)$/);
  if (!match) return undefined;
  const mime = match[1] ?? 'application/octet-stream';
  const isBase64 = (match[2] ?? '').includes('base64');
  const payload = match[3] ?? '';
  const bytes = isBase64
    ? Uint8Array.from(atob(payload), (c) => c.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(payload));
  const blob = new Blob([bytes], { type: mime });
  const ext = (mime.split('/')[1] ?? 'bin').replace(/[^a-z0-9]/gi, '');
  const fileName = `upload.${ext}`;

  const result = await imageUploadService.upload(blob, fileName);
  // Use `downloadUrl` (the real fetchable route, e.g. `/api/Image/<sha>.png`)
  // and not `url` (the server-side storage path which 404s as a relative
  // URL). The auth-aware <AuthImg> component picks up the Bearer header
  // automatically — see G61 in BACKEND_GAPS.md.
  return result.downloadUrl ?? toAbsoluteImageUrl(result.url);
}

/**
 * Make a tenant-relative `/uploads/...` URL absolute, anchored to
 * the API host. In dev mode the API client's `baseURL` is `/api`
 * (Vite proxies it), so the API origin equals the page origin and
 * we return the relative path unchanged — works as long as Vite is
 * also proxying `/uploads/*`.
 *
 * In production `baseURL` is either `/api` (canonical — IIS reverse-
 * proxies to `https://api.ebillbook.kristysoftware.com/api`) or the
 * absolute API URL when `VITE_API_BASE_URL` overrides it. Either way
 * we strip the trailing `/api` and prepend to make the upload path
 * absolute.
 */
export function toAbsoluteImageUrl(value: string): string {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value; // already absolute
  if (!value.startsWith('/')) return value; // some other format — leave it
  const base = String(apiClient.defaults.baseURL ?? '').replace(/\/api\/?$/, '');
  // base is either '' (dev: '/api' → '') or 'https://host' (prod).
  return base + value;
}
